import { useCallback, useContext, useEffect, useRef, useState } from "react";
import CompanyService from "../services/CompanyService";
import RequestService from "../services/RequestService";
import UserService from "../services/UserService";
import { TranslationService } from "../services/TranslationService";
import ToastContext from "../pages/shared/bootstrap/Toast";

// The toggle reloads the page, so the confirmation toast is shown after the reload
const PENDING_TOAST_KEY = "viewAllSegmentsPendingToast";

const savePendingToast = (viewAll: boolean) => {
  try {
    sessionStorage.setItem(PENDING_TOAST_KEY, viewAll ? "all" : "own");
  } catch {
    // Storage unavailable: the change still applies, only the toast is lost
  }
};

const takePendingToast = (): "all" | "own" | null => {
  try {
    const value = sessionStorage.getItem(PENDING_TOAST_KEY);
    sessionStorage.removeItem(PENDING_TOAST_KEY);
    return value === "all" || value === "own" ? value : null;
  } catch {
    return null;
  }
};

/**
 * Handles the "View all segments" user toggle.
 * The token-based conditions are resolved synchronously; the user data
 * (`/user/get`) is fetched once on mount, and only when those conditions
 * already allow the toggle, so the result is ready before the UI needs it.
 */
export const useViewAllSegments = () => {
  const [eligible] = useState(
    () =>
      !!CompanyService.getSetting("allowsegmentfiltertoggle") &&
      !!CompanyService.getHasSegments()
  );
  const [hasLimitedSegments, setHasLimitedSegments] = useState(false);
  const [value, setValue] = useState<boolean>(
    CompanyService.getFilterUserIdPaused()
  );
  const [loading, setLoading] = useState(eligible);
  const mounted = useRef(true);
  const { showToast } = useContext(ToastContext);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const pending = takePendingToast();
    if (!pending) return;
    showToast?.(
      pending === "all"
        ? TranslationService.translate.ShowingAllSegments
        : TranslationService.translate.ShowingOwnSegments,
      undefined,
      "success"
    );
    // Only on mount: the pending toast is consumed once after the reload
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!eligible) {
      return;
    }

    const checkUserSegments = async () => {
      setLoading(true);
      try {
        const userId = CompanyService.getUserid();
        if (!userId) {
          setHasLimitedSegments(false);
          return;
        }
        const userResult = await UserService.get(userId);
        if (!mounted.current) return;
        if (userResult && userResult.item) {
          // The user has limited segments when at least one is assigned
          const hasSegments =
            !!userResult.item.segments && userResult.item.segments.length > 0;
          setHasLimitedSegments(hasSegments);
        } else {
          setHasLimitedSegments(false);
        }
      } catch (error) {
        if (mounted.current) setHasLimitedSegments(false);
      } finally {
        if (mounted.current) setLoading(false);
      }
    };

    checkUserSegments();
  }, [eligible]);

  const toggle = useCallback(async () => {
    const newValue = !value;
    setValue(newValue);
    try {
      await RequestService.togglefilteruseridpaused(newValue);
      savePendingToast(newValue);
      // Reload the page to apply the change
      window.location.reload();
    } catch (error) {
      // Revert the change on error
      setValue(!newValue);
      console.error("Error toggling the view of all segments:", error);
    }
  }, [value]);

  const visible = eligible && hasLimitedSegments && !loading;

  return { visible, loading, value, toggle };
};
